import React from 'react';
import { Card, List, Tag, Typography } from 'antd';
import { AIAnalysisResult } from '../../Questionnaire/NewAI/shared/types';
import { 
  formatEndoteleonName, 
  formatBiochemicalName,
  t 
} from './renderingHelpers';

const { Title, Paragraph, Text } = Typography;

interface AIAnalysisResultPrompt1Props {
  result: AIAnalysisResult;
  lang: 'en' | 'nl';
}

export const AIAnalysisResultPrompt1: React.FC<AIAnalysisResultPrompt1Props> = ({ result, lang }) => {
  return (
    <>
      {/* A1 Section - Primary and Secondary Analysis */}
      <div style={{ marginBottom: 24 }}>
        <Title level={5}>{t(lang, 'a1Title')}</Title>
        <Card size="small" style={{ marginBottom: 16 }}>
          <div>
            <Text strong>{t(lang, 'primary')}</Text>
            <div style={{ marginTop: 8, marginBottom: 16 }}>
              <Tag color="blue" style={{ fontSize: '14px', padding: '4px 8px' }}>
                {formatEndoteleonName(result.A1.primary_ie)}
              </Tag>
            </div>
            
            <Text strong>{t(lang, 'secondary')}</Text>
            <div style={{ marginTop: 8, marginBottom: 16 }}>
              {result.A1.secondary_ie.map((item: string, index: number) => (
                <Tag key={index} color="green" style={{ fontSize: '14px', padding: '4px 8px', marginBottom: '4px' }}>
                  {formatEndoteleonName(item)}
                </Tag>
              ))}
            </div>
            
            <Text strong>{t(lang, 'teleonic')}</Text>
            <Paragraph style={{ marginTop: 8, backgroundColor: '#f5f5f5', padding: '12px', borderRadius: '6px' }}>
              {result.A1.teleonic_pattern}
            </Paragraph>

            <Text strong>{t(lang, 'uncertainty')}</Text>
            <div style={{ marginTop: 8 }}>
              <Tag color={result.A1.uncertainty_level > 0.7 ? 'red' : result.A1.uncertainty_level > 0.4 ? 'orange' : 'green'}>
                {result.A1.uncertainty_label}
              </Tag>
            </div>
          </div>
        </Card>
      </div>

      {/* A2 Section - Endoteleon Priority List */}
      {result.A2 && result.A2.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <Title level={5}>
            {lang === 'en' ? 'A2: Endoteleon Priority List' : 'A2: Endoteleon Prioriteitenlijst'}
          </Title>
          <Card size="small">
            <List
              dataSource={result.A2}
              renderItem={(item: string, index: number) => (
                <List.Item>
                  <div style={{ width: '100%', display: 'flex', alignItems: 'center' }}>
                    <Text strong style={{ marginRight: 16 }}>{index + 1}.</Text>
                    <Tag color="purple" style={{ fontSize: '14px', padding: '4px 8px' }}>
                      {formatEndoteleonName(item)}
                    </Tag>
                  </div>
                </List.Item>
              )}
            />
          </Card>
        </div>
      )}

      {/* A3 Section - Biochemical Markers */}
      <div style={{ marginBottom: 24 }}>
        <Title level={5}>{t(lang, 'a3Title')}</Title>
        <Card size="small">
          <div style={{ marginBottom: 16 }}>
            <Text strong>{t(lang, 'directMarkers')}</Text>
            <div style={{ marginTop: 8 }}>
              {result.A3?.direct?.map((marker: string, index: number) => (
                <Tag key={index} color="orange" style={{ fontSize: '14px', padding: '4px 8px', marginRight: '8px', marginBottom: '4px' }}>
                  {formatBiochemicalName(marker)}
                </Tag>
              ))}
            </div>
          </div>
          <div>
            <Text strong>{t(lang, 'indirectMarkers')}</Text>
            <div style={{ marginTop: 8 }}>
              {result.A3?.indirect?.map((marker: string, index: number) => (
                <Tag key={index} color="cyan" style={{ fontSize: '14px', padding: '4px 8px', marginRight: '8px', marginBottom: '4px' }}>
                  {formatBiochemicalName(marker)}
                </Tag>
              ))}
            </div>
          </div>
        </Card>
      </div>

      {/* B1 Section - Recommended Questionnaires (Primary) */}
      
        <div style={{ marginBottom: 24 }}>
          <Title level={5}>
            {lang === 'en' ? 'B1: Primary Recommended Questionnaires' : 'B1: Primaire Aanbevolen Vragenlijsten'}
          </Title>
          <Card size="small">
            <List
              dataSource={result.B1}
              renderItem={(questionnaire: any) => (
                <List.Item>
                  <div style={{ width: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                      <Text strong style={{ marginRight: 12 }}>{questionnaire.nr}.</Text>
                      <Text strong>{questionnaire.label}</Text>
                    </div>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      ID: {questionnaire.ID || questionnaire.id || 'N/A'}
                    </Text>
                  </div>
                </List.Item>
              )}
            />
          </Card>
        </div>
      

      {/* B2 Section - Recommended Questionnaires (Secondary) */}
        <div style={{ marginBottom: 24 }}>
          <Title level={5}>
            {lang === 'en' ? 'B2: Secondary Recommended Questionnaires' : 'B2: Secundaire Aanbevolen Vragenlijsten'}
          </Title>
          <Card size="small">
            <List
              dataSource={result.B2}
              renderItem={(questionnaire: any) => (
                <List.Item>
                  <div style={{ width: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                      <Text strong style={{ marginRight: 12 }}>{questionnaire.nr}.</Text>
                      <Text strong>{questionnaire.label}</Text>
                    </div>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      ID: {questionnaire.ID || questionnaire.id || 'N/A'}
                    </Text>
                  </div>
                </List.Item>
              )}
            />
          </Card>
        </div>

      {/* B3 Section - HETA Recommendations */}
        <div style={{ marginBottom: 24 }}>
          <Title level={5}>
            {lang === 'en' ? 'B3: HETA Recommendations' : 'B3: HETA Aanbevelingen'}
          </Title>
          <Card size="small">
            <List
              dataSource={result.B3}
              renderItem={(heta: any) => (
                <List.Item>
                  <div style={{ width: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                      <Text strong style={{ marginRight: 12 }}>{heta.nr}.</Text>
                      <Text strong>{lang === 'en' ? 'HETA:' : 'HETA:'}</Text>
                      <Tag color="gold" style={{ marginLeft: 8 }}>
                        {heta.heta_json}
                      </Tag>
                    </div>
                    {heta.tier && (
                      <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                        {lang === 'en' ? 'Tier:' : 'Tier:'} {heta.tier}
                      </Text>
                    )}
                    {heta.heta_id && (
                      <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                        {lang === 'en' ? 'ID:' : 'ID:'} {heta.heta_id}
                      </Text>
                    )}
                  </div>
                </List.Item>
              )}
            />
          </Card>
        </div>
    </>
  );
};

export default AIAnalysisResultPrompt1;
